/**
 * Tree utility functions for FamiliesPage
 * Used for building hierarchical tree structures from flat feature lists
 */

import { sortByName } from './sortingUtils';

/**
 * Recursively sort feature arrays under each tree node alphabetically by name
 * @param {Object} node - Tree node
 */
function sortTreeNodeFeatures(node) {
    if (!node) return;
    if (Array.isArray(node.features)) {
        node.features = sortByName(node.features, 'name');
    }
    Object.values(node.children || {}).forEach(sortTreeNodeFeatures);
}

/**
 * Build a tree structure from a flat list of features
 * Features are organized by dot-notation in their names (e.g., "Parent.Child.Grandchild")
 * @param {Array} features - Flat array of feature objects
 * @param {string} searchQuery - Optional search query to filter features
 * @returns {Object} Tree structure with nested children and features
 */
export function buildTreeFromFeatures(features, searchQuery = '') {
    const root = { name: 'Root', children: {}, features: [] };
    
    // Filter features by search query if provided, then sort for stable alphabetical insertion
    let filteredList = sortByName([...features], 'name');
    if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        filteredList = filteredList.filter(f => 
            f.name.toLowerCase().includes(q) || 
            (f.property?.displayText || '').toLowerCase().includes(q)
        );
    }
    
    // Parse into tree structure split by dot-notation
    filteredList.forEach(v => {
        const parts = v.name.split('.');
        let current = root;
        
        parts.forEach((part, index) => {
            if (index === parts.length - 1) {
                // Leaf node - add as a feature
                if (!current.features) current.features = [];
                current.features.push({
                    name: part,
                    fullName: v.name,
                    label: v.property?.displayText || '',
                    id: v.id,
                    raw: v
                });
            } else {
                // Intermediate node - create or traverse to child folder
                if (!current.children[part]) {
                    current.children[part] = { name: part, children: {}, features: [] };
                }
                current = current.children[part];
            }
        });
    });

    sortTreeNodeFeatures(root);
    return root;
}

/**
 * Check if a tree node has any children or features
 * @param {Object} node - Tree node to check
 * @returns {boolean} True if node has children or features
 */
export function hasNodeContent(node) {
    return node && (
        Object.keys(node.children || {}).length > 0 ||
        (node.features || []).length > 0
    );
}

/**
 * Count total features in a tree (including nested)
 * @param {Object} node - Tree node to count features from
 * @returns {number} Total count of features
 */
export function countFeaturesInTree(node) {
    if (!node) return 0;
    
    let count = (node.features || []).length;
    
    Object.values(node.children || {}).forEach(child => {
        count += countFeaturesInTree(child);
    });
    
    return count;
}

/**
 * Find a feature in the tree by its ID
 * @param {Object} node - Tree node to search from
 * @param {string} featureId - ID of feature to find
 * @returns {Object|null} The feature object if found, or null
 */
export function findFeatureInTree(node, featureId) {
    if (!node) return null;
    
    // Check features at this level
    const foundInFeatures = (node.features || []).find(f => f.id === featureId);
    if (foundInFeatures) return foundInFeatures;
    
    // Recursively check children
    for (const childKey in node.children || {}) {
        const result = findFeatureInTree(node.children[childKey], featureId);
        if (result) return result;
    }
    
    return null;
}

/**
 * Get all features from a tree as a flat array (depth-first)
 * @param {Object} node - Tree node to flatten
 * @param {string} path - Current path prefix (used internally for recursion)
 * @returns {Array} Flat array of all features
 */
export function flattenTreeFeatures(node, path = '') {
    const features = [];
    
    if (!node) return features;
    
    // Add features at this level with their full path
    (node.features || []).forEach(f => {
        features.push({
            ...f,
            path: path ? `${path}.${f.name}` : f.name
        });
    });
    
    // Recursively process children
    Object.entries(node.children || {}).forEach(([name, childNode]) => {
        const childPath = path ? `${path}.${name}` : name;
        features.push(...flattenTreeFeatures(childNode, childPath));
    });
    
    return features;
}
